import { ref, computed } from 'vue';
import { useTimeStore } from '@/store/pinia/time';
import { useUsersStore } from '@/store/pinia/users';
import { usePhaseStore } from '@/store/pinia/phase';
import { useSidebarStore } from '../store/pinia/sidebar';
import { storeToRefs } from 'pinia';

export function useTimeDisplay(options) {
  const timeStore = useTimeStore();
  const usersStore = useUsersStore();
  const phaseStore = usePhaseStore();
  const sidebarStore = useSidebarStore();
  const {
    times,
    phaseRemaining,
    phaseLength,
    alarmRunning,
    tick: tickRaw,
    settingTime: rawSettingTime,
    quickSettingProp,
    snappedSettingTime,
    meetingLength,
    meetingRemaining,
    iAmSetting,
  } = storeToRefs(timeStore);
  const { iAmStillAway, iAmStillAwayInGroup, iAmAway } = storeToRefs(usersStore);
  const { breakEndedEarly, groupsReassemble, rawCurrentPhase, currentPhase, settingUp, setupPhase, preparing } = storeToRefs(phaseStore);
  const { isAnimating: sidebarAnimating } = storeToRefs(sidebarStore);

  const { type = 'phase' } = options;
  options = {
    r: 106,
    setting: false,
    ...options,
  };

  const r = ref(options.r);

  const setting = computed(() => {
    if (iAmStillAway.value || iAmStillAwayInGroup.value) {
      return false;
    } else if (settingUp.value && !preparing.value && type === 'phase') {
      return true;
    } else if (type === 'meetingSetter') {
      return true;
    } else {
      return false;
    }
  });

  const remaining = computed(() => {
    if (type === 'phase') {
      return phaseRemaining.value;
    } else {
      return meetingRemaining.value;
    }
  });

  const total = computed(() => {
    if (type === 'phase') {
      return phaseLength.value;
    } else {
      return meetingLength.value;
    }
  });

  const timeData = computed(() => {
    if (type === 'meetingSetter') {
      return { ...times.value.meeting };
    } else if (settingUp.value) {
      let timerDetails = { ...times.value[setupPhase.value] };
      if (settingTime.value) {
        timerDetails.defaultTime = settingTime.value;
      }
      return timerDetails;
    } else {
      return { ...times.value[currentPhase.value] };
    }
  });

  const settingTime = computed(() => {
    return rawSettingTime.value || timeData.value?.defaultTime;
  });
  const settingProp = computed(() => {
    if (iAmSetting.value) {
      return quickSettingProp.value;
    } else {
      return settingTime.value / timeData.value.max;
    }
  });
  const runningProp = computed(() => remaining.value / total.value);

  const prop = computed(() => {
    if (iAmSetting.value) {
      return quickSettingProp.value;
    } else {
      return setting.value ? settingProp.value : runningProp.value;
    }
  });
  const statusClass = computed(() => {
    if (
      breakEndedEarly.value ||
      groupsReassemble.value ||
      iAmStillAway.value ||
      iAmStillAwayInGroup.value ||
      (rawCurrentPhase.value !== 'breakTime' && iAmAway.value)
    ) {
      return 'overdue';
    } else if (setting.value || preparing.value || total.value === 0 || sidebarAnimating.value || currentPhase.value === 'freeform') {
      return false;
    } else {
      if (remaining.value <= 0) {
        return 'overdue';
      } else if (prop.value < 0.25) {
        return 'end';
      } else if (prop.value < 0.5) {
        return 'mid';
      } else {
        return 'start';
      }
    }
  });

  const jangle = computed(() => (alarmRunning.value && type === 'phase' ? 'jangle' : false));

  const tickMs = computed(() => tick.value + 'ms');

  const barEasing = computed(() => (setting.value ? 'cubic-bezier(0.83, 0, 0.17, 1)' : 'linear'));

  const tick = computed(() => {
    if (!setting.value) {
      return tickRaw.value;
    } else {
      return iAmSetting.value ? 0 : 500;
    }
  });

  const c = computed(() => Math.PI * r.value * 2);
  const dash = computed(() => {
    return c.value * (1 - prop.value);
  });

  return { c, dash, prop, remaining, statusClass, jangle, tickMs, barEasing, setting, settingProp, settingTime, snappedSettingTime, timeData };
}
